<template>
  <div class="border-container">

    <Panel toggleable v-model:collapsed="collapsed" style="width: 260px; display: inline-block">
      <template #header>
        <div style="width: 50px; display: inline-block;">{{ label }}</div>
        <InputNumber v-model="topOrAll" showButtons class="number" :placeholder="placeholder" :inputStyle="{width: inputWidth }"/>
        <div style="width: 70px; display: inline-block" v-if="color">
            <ColorButton @click="colorPickerVisible.toggle($event)" :color="borderColor" style="flex: 3"  />
            <OverlayPanel ref="colorPickerVisible">
              <ColorPicker v-model="borderColor"
                           @edit-theme="$refs.colorPickerVisible.toggle(false); themeDialogVisible = true"
              />
            </OverlayPanel>
        </div>
      </template>
      <div style="padding-left: 10px;">
        <InputNumber v-model="left" showButtons class="border-inputs"/>
        <div style="width: 10px; display: inline-block;"></div>
        <InputNumber v-model="right" showButtons class="border-inputs"/>
      </div>
      <div style="padding-left: 80px; padding-top: 10px">
        <InputNumber v-model="bottom" showButtons class="border-inputs"/>
      </div>
    </Panel>
    <Button v-if="canDelete" icon="pi pi-trash" @click="clear" class="trash"></Button>
  </div>
</template>

<script setup lang="ts">
import {ref, defineProps, computed, watch, nextTick} from 'vue'
import InputNumber from "primevue/inputnumber";

import Panel from 'primevue/panel';
import Button from "primevue/button"
import {storeToRefs} from "pinia";
import {useSlidesStore, useWidgetStore, useMainStore} from "@/store";
import ColorButton from "@/views/Editor/Toolbar/common/ColorButton.vue";
import ColorPicker from "@/components/ColorPicker/index.vue";
import {PPTDragDropElement, PPTSlideLayoutElement} from "@/types/slides";
import {useMicroservice} from "@/hooks";
import _ from 'lodash';

const widgetStore = useWidgetStore()
const slidesStore = useSlidesStore()
const api = useMicroservice()
const { selectedWidget, selectedTableCell, textSelected, alignment, inputSelected, selectedSectionId, selectedSectionRowId, selectedSubsectionId, selectedSubsectionRowId, selectedDragDropId } = storeToRefs(widgetStore)
const inputWidth = computed(() => collapsed.value && !props.color ? '100px' : '50px')
const { themeDialogVisible } = storeToRefs(useMainStore())



const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  attribute: {
    type: String,
    required: true,
  },
  color: {
    type: Boolean,
    required: false,
    default: false
  },
  elementType: {
    type: String,
    required: true
  },
  mappings: {
    type: Object,
    required: true
  },
})

const collapsed = ref(true)

const element = computed(() => {
  if (props.elementType === 'element') {
    return selectedWidget.value
  }

  if (props.elementType === 'subsection') {
    if (selectedSubsectionId.value) {

      const subsection = findSubsection()
      if (subsection) {
        subsection.type = 'subsection'
        if (!('style' in subsection)) {
          subsection.style = {}
        }
        return subsection
      }
      return { type: 'subsection', style: {} }
    }
  }

  if (props.elementType === 'section') {
    if (selectedSectionId.value) {

      const section = findSection()
      if (section) {
        section.type = 'section'
        if (!('style' in section)) {
          section.style = {}
        }
        return section
      }
      return { type: 'section', style: {} }
    }
  }

  return null
}) as any | null

const getSelectedDragDrop = () => {
  if (selectedDragDropId.value) {
    for (const dragdrop of slidesStore.getElementsByType('dragdrop')) {
      if (dragdrop.id === selectedDragDropId.value) {
        return <PPTDragDropElement>dragdrop
      }
    }
  }

  return <PPTDragDropElement>slidesStore.getElementsByType('dragdrop')[0]
}

const getSlideLayoutElement = () => {
  return slidesStore
      .getElementsByType('slidelayout')
      .find((element) =>
          element.layout.children.some((row) =>
              row.children.some((section) => section.id === selectedSectionId.value)
          )
      )
}


const findSection = () => {
  const slideLayoutElement: PPTSlideLayoutElement = <PPTSlideLayoutElement>getSlideLayoutElement()
  if (slideLayoutElement) {
    const row = slideLayoutElement.layout.children.find((row) => row.id == selectedSectionRowId.value)
    if (row) {
      return row.children.find((col) => col.id === selectedSectionId.value)
    }
  }
  return {}
}


const findSubsection = () => {
  const dragDropElement = getSelectedDragDrop()
  const row = dragDropElement.layout.find((row) => row.id === selectedSubsectionRowId.value)
  if (row) {
    return row.children.find((col) => col.id === selectedSubsectionId.value)
  }
}

const createDimension = (key) => computed({
  get: () => element.value?.style[key],
  set: val => {
    element.value && (element.value.style[key] = val)
    element.value.style[allMapping] = null
  }
})



const allMapping = props.mappings.all ? props.mappings.all : props.mappings.prefix
const all = computed({
  get: () => element.value?.style[allMapping],
  set: val => {
    element.value && (element.value.style[allMapping] = val)
  }
})

watch(all, (newVal, prevVal) => {
  if (!prevVal && newVal > 0 && (borderColor.value === null || borderColor.value === "transparent")) {
    borderColor.value = '#808080'
  }
})

const top = createDimension(`${props.mappings.prefix}Top`)
const left = createDimension(`${props.mappings.prefix}Left`)
const right = createDimension(`${props.mappings.prefix}Right`)
const bottom = createDimension(`${props.mappings.prefix}Bottom`)

const placeholder = computed(() => {
  if (collapsed.value && !all.value && (top.value || left.value || right.value || bottom.value || '')) {
    return  (top.value || 0) + ','
        + (left.value || 0) + ','
        + (right.value || 0) + ','
        + (bottom.value || 0)
  }
  else {
    return ''
  }
})

const canDelete = computed(() => all.value || top.value || left.value || right.value || bottom.value)

const topOrAll = computed({
  get: () => {
    if (!all.value && top.value && top.value === left.value && top.value === right.value && top.value === bottom.value) {
      all.value = top.value
    }
    return collapsed.value ? all.value : top.value
  },
  set: val => {
    if (collapsed.value) {
      all.value = val
    }
    else {
      top.value = val
    }
  }
})

const clear = () => {
  all.value = null
  top.value = null
  left.value = null
  right.value = null
  bottom.value = null
}

watch(collapsed, () => {
  if (collapsed.value) {
    if (top.value && top.value === left.value && top.value === right.value && top.value === bottom.value) {
      all.value = top.value
    }
    else {
      all.value = null
    }
  }
  else {
    if (all.value) {
      const myvalue = all.value
      top.value = myvalue
      left.value = myvalue
      right.value = myvalue
      bottom.value = myvalue
    }
  }
})


if (props.elementType === 'element') {
  watch(selectedWidget, () => {
    if (!collapsed.value && all.value && !top.value && !left.value && !right.value && !bottom.value) {
      nextTick(() => {
        top.value = all.value
        right.value = all.value
        bottom.value = all.value
        left.value = all.value
      })
    }
  })
}


const colorPickerVisible = ref()
const borderColor = computed({
  get: () => element.value?.style?.borderColor ?? 'transparent',
  set: val => element.value && (element.value.style.borderColor = val)
})

const headerBorderColor = computed(() => collapsed.value ? '#dee2e6' : 'transparent')

const debouncedSave = _.debounce(async () => {
  await api.savePresentation();
}, 1000);

watch([
  topOrAll,
  all,
  top,
  bottom,
  right,
  left,
  borderColor
], () => {
  debouncedSave()
})

</script>
<style scoped>
.border-container { width: 350px; display: flex; align-items: center; color: #666 }
.trash { margin-left: 5px; }
:deep(.p-panel-header) { padding-left: 8px !important; padding-right: 8px !important; background: transparent;
  border-bottom-color: v-bind(headerBorderColor);
}

.border-inputs :deep(.p-inputtext) {
  width: 50px;
}
</style>
